import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    InputBase,
    IconButton,
    Grid,
    Menu,
    MenuItem,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import DownloadUtils from '../../../../utils/download-utils';
import FavouriteButtonActive from '../../../../assets/icons/FavouriteButtonActive.svg';
import FavouriteButtonDisabled from '../../../../assets/icons/FavouriteButtonDisabled.svg';
import EditHandoutIcon from '../../../../assets/icons/CustomiseHandout.svg';
import ShareEnvelopIcon from '../../../../assets/icons/ShareEnvelop.svg';
import BinArchiveIcon from '../../../../assets/icons/BinArchive.svg';
import PatientActionsIcon from '../../../../assets/icons/Archive.svg';
import { archiveCustomisedHandout } from '../../../helper/customisedHandoutUtils';
import QuickShareDialog from '../quick-share-dialog';
import AddPatientDialog from './add-patient-modal';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { CLINIC_PATIENT_ID_LABEL, PATIENT_EMAIL_LABEL, PATIENT_LABEL } from '../../../helper/patientLabels';
import {
    isSavedHandoutFavourite,
    toggleSavedHandoutFavourite,
} from '../../../../utils/saved-handout-favourites';

/** Pagination helper — visible page numbers with ellipsis */
const getVisiblePages = (currentPage, pageCount) => {
    if (pageCount <= 5) return Array.from({ length: pageCount }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 'ellipsis', pageCount];
    if (currentPage >= pageCount - 2) return [1, 'ellipsis', pageCount - 2, pageCount - 1, pageCount];
    return [1, 'ellipsis', currentPage, 'ellipsis', pageCount];
};

/** Formats Unix timestamp or ISO date string to readable date */
const formatDate = (val) => {
    if (!val) return null;
    const date = typeof val === 'number' ? new Date(val) : new Date(val);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Shows tooltip only when text visually overflows its container */
const TruncatedTooltip = ({ text, onClick }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    useEffect(() => {
        const el = textRef.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [text]);
    return (
        <Tooltip title={text} placement="top" arrow disableHoverListener={!isTruncated}>
            <Typography
                ref={textRef}
                onClick={onClick}
                sx={{
                    fontSize: '13px',
                    lineHeight: '18px',
                    color: '#616063',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    width: '100%',
                }}
            >
                {text}
            </Typography>
        </Tooltip>
    );
};

/**
 * Patient summary card — displayed at the top of the handout view screen.
 * White background, same layout as my-clinic PatientCard but without the "View handouts" button.
 */
const PatientSummaryCard = ({ patient, totalHandoutCount, onEdit, onArchive }) => {
    const dateStr = formatDate(patient.lastUpdated);
    const patientFullName = [patient.patientName, patient.patientSurname].filter(Boolean).join(' ') || 'N/A';
    const patientInfo =
        [
            patient.patientEmail ? `${PATIENT_EMAIL_LABEL}: ${patient.patientEmail}` : null,
            patient.clinicPatientId ? `${CLINIC_PATIENT_ID_LABEL}: ${patient.clinicPatientId}` : null,
        ]
            .filter(Boolean)
            .join(' • ') || 'No additional patient details';
    const isArchived = patient.patientStatus === 'archived';

    return (
        <Box
            sx={{
                background: '#FFFFFF',
                borderRadius: { xs: '10px', md: '20px' },
                pt: { xs: '2px', md: '14px' },
                pb: { xs: '8px', md: '14px' },
                px: { xs: '10px', md: '16px' },
                border: '1px solid #ECECFA',
                minHeight: { xs: '91px', md: '160px' },
                display: 'flex',
                flexDirection: 'row',
                gap: { xs: '8px', md: '12px' },
            }}
        >
            {/* Left column: patient name, patient info, spacer, handout count, date */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box sx={{ mb: { xs: '2.34px', md: '10px' } }}>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '12px', md: '24px' },
                            fontWeight: 600,
                            color: '#000453',
                            letterSpacing: { xs: '-0.23px', md: '-0.48px' },
                            lineHeight: { xs: '12px', md: '24px' },
                        }}
                    >
                        {patientFullName}
                    </Typography>{' '}
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '8px', md: '14px' },
                            fontWeight: 500,
                            color: 'rgba(97,96,99,0.5)',
                            lineHeight: { xs: '8px', md: '14px' },
                        }}
                    >
                        {PATIENT_LABEL}
                    </Typography>
                </Box>
                <Box sx={{ minHeight: { xs: '32px', md: 'auto' } }}>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '12px', md: '20px' },
                            fontWeight: 600,
                            color: '#000453',
                            letterSpacing: { xs: '-0.19px', md: '-0.4px' },
                            lineHeight: { xs: '16px', md: '20px' },
                        }}
                    >
                        {patientInfo}
                    </Typography>{' '}
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '8px', md: '14px' },
                            fontWeight: 500,
                            color: 'rgba(97,96,99,0.5)',
                            lineHeight: { xs: '8px', md: '14px' },
                        }}
                    >
                            Details
                    </Typography>
                </Box>
                {/* Spacer — pushes footer content down; minHeight reserves empty line for overflow on mobile */}
                <Box sx={{ flex: 1, minHeight: { xs: '16px', md: 0 } }} />
                <Typography
                    sx={{
                        fontSize: { xs: '9.615px', md: '20px' },
                        fontWeight: 400,
                        color: '#FF669B',
                        letterSpacing: { xs: '-0.192px', md: '-0.4px' },
                        lineHeight: { xs: '9.615px', md: '20px' },
                    }}
                >
                    {totalHandoutCount} handout{totalHandoutCount !== 1 ? 's' : ''} assigned
                </Typography>
                {dateStr && (
                    <Typography
                        component="span"
                        sx={{
                            display: 'block',
                            mt: { xs: '7.385px', md: '30px' },
                            fontSize: { xs: '8px', md: '14px' },
                            fontWeight: 500,
                            color: '#616063',
                            opacity: 0.5,
                            letterSpacing: { xs: '-0.16px', md: '-0.28px' },
                            lineHeight: { xs: '8px', md: '14px' },
                        }}
                    >
                        Updated {dateStr}
                    </Typography>
                )}
            </Box>

            {/* Right column: edit + archive icons only (no View handouts button) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                {/* Composite Archive.svg: edit (left circle) + archive (right circle); archived patients only show edit */}
                <Box
                    sx={{
                        position: 'relative',
                        width: isArchived ? { xs: 27, md: 50 } : { xs: 59, md: 110 },
                        height: { xs: 27, md: 50 },
                        flexShrink: 0,
                        mt: { xs: '6px', md: '5px' },
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="img"
                        src={PatientActionsIcon}
                        alt="Patient actions"
                        sx={{ width: { xs: 59, md: 110 }, height: '100%', display: 'block' }}
                    />
                    <Box
                        onClick={() => onEdit(patient)}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: { xs: 27, md: 50 },
                            height: '100%',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            '&:hover': { background: 'rgba(255,255,255,0.15)' },
                        }}
                    />
                    {!isArchived && (
                        <Box
                            onClick={() => onArchive(patient.patientId)}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                width: { xs: 27, md: 50 },
                                height: '100%',
                                cursor: 'pointer',
                                borderRadius: '50%',
                                '&:hover': { background: 'rgba(255,255,255,0.15)' },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

/**
 * Patient handout library screen (CETRA-728).
 * Reached from /my-clinic via "View handouts" → /my-clinic-patient-library/:patientId
 * Shows a patient summary card and a library panel of customised handouts assigned to that patient.
 * Scoped to the authenticated user — in multiuser clinic plans the store data is already user-scoped.
 */
const MyClinicPatientLibrary = () => {
    const navigate = useNavigate();
    const { patientId } = useParams();
    const { userDataStore, authStore, imageStore, contentStore, customisedHandoutStore, savedHandoutStore } =
        useContext(StoreContext);
    const showSnackbar = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [patient, setPatient] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);

    // Handout library state
    const [customisedHandouts, setCustomisedHandouts] = useState([]);
    const [savedHandouts, setSavedHandouts] = useState([]);
    const [savedHandoutImageMap, setSavedHandoutImageMap] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('RECENT');
    const [sortDirection, setSortDirection] = useState('desc');
    const [hasSortedRecently, setHasSortedRecently] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [itemsPerPage, setItemsPerPage] = useState(4);
    const [currentPage, setCurrentPage] = useState(1);
    const [imageMap, setImageMap] = useState({});
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [noteTarget, setNoteTarget] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [quickShareHandout, setQuickShareHandout] = useState(null);
    const [isDeletingSavedHandout, setIsDeletingSavedHandout] = useState(false);

    const fetchedImages = useRef(new Set());
    const fetchedSavedImages = useRef(new Set());

    // Load patient from sessionStorage; fall back to API fetch
    useEffect(() => {
        const stored = sessionStorage.getItem('myClinicSelectedPatient');
        if (stored) {
            setPatient(JSON.parse(stored));
            sessionStorage.removeItem('myClinicSelectedPatient');
        } else {
            // Direct URL navigation — fetch patient from API
            const fetchPatient = async () => {
                setLoading(true);
                try {
                    const result = await UserApi.getPatient(authStore.idToken, patientId);
                    if (result) setPatient(result);
                } catch (err) {
                    console.error('Failed to load patient', err);
                    showSnackbar('Failed to load patient.', 'error');
                } finally {
                    setLoading(false);
                }
            };
            fetchPatient();
        }
    }, [patientId, authStore.idToken, showSnackbar]);

    // Load customised handouts for this patient from the store
    useEffect(() => {
        if (!customisedHandoutStore?.isInitialized) return;

        const data = Object.values(customisedHandoutStore.customisedHandoutData)
            .filter((ch) => ch.customisedStatus === 'active' && ch.patientId === patientId)
            .sort((a, b) => b.lastUpdated - a.lastUpdated);

        setCustomisedHandouts(data);
    }, [customisedHandoutStore?.customisedHandoutData, customisedHandoutStore?.isInitialized, patientId]);

    useEffect(() => {
        if (!savedHandoutStore?.isInitialized) return;

        const data = Object.values(savedHandoutStore.savedHandoutData)
            .filter((item) => item.savedStatus === 'active' && item.patientId === patientId)
            .sort((a, b) => b.lastUpdated - a.lastUpdated);

        setSavedHandouts(data);
    }, [savedHandoutStore?.savedHandoutData, savedHandoutStore?.isInitialized, patientId]);

    // Reset to page 1 when search/sort/page size changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortBy, sortDirection, itemsPerPage]);

    const getDocumentTitle = (item) => {
        const contentData = item.effectiveContentData?.['en-UK'];
        if (!contentData || !Array.isArray(contentData) || contentData.length === 0) return 'Untitled Handout';
        const firstSection = contentData[0];
        if (!firstSection?.blocks) return 'Untitled Handout';
        return firstSection.blocks.find((block) => block.type === 'documentTitle')?.value || 'Untitled Handout';
    };

    const getFirstParagraph = (item) => {
        const contentData = item.effectiveContentData?.['en-UK'];
        if (!contentData || !Array.isArray(contentData)) return '';
        for (const section of contentData) {
            if (!section.blocks) continue;
            for (const block of section.blocks) {
                if (
                    (block.type === 'paragraph' || block.type === 'text' || block.type === 'textBullet') &&
                    block.value
                ) {
                    return block.value.trim();
                }
            }
        }
        return '';
    };

    const getImageSource = (item) => {
        if (item.type === 'saved') {
            const imageSrc = getSavedHandoutImageSrcKey(item);
            return imageSrc ? savedHandoutImageMap[imageSrc] || null : null;
        }
        if (item.attachedWhiteboardCanvasImageLink && imageMap[item.attachedWhiteboardCanvasImageLink]) {
            return imageMap[item.attachedWhiteboardCanvasImageLink];
        }
        const contentData = item.effectiveContentData?.['en-UK'];
        if (!contentData || !Array.isArray(contentData)) return null;
        for (const section of contentData) {
            if (!section.blocks) continue;
            const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
            if (illustrationBlock?.src) return imageMap[illustrationBlock.src] || null;
        }
        return null;
    };

    const getSavedBaseHandout = useCallback(
        (savedHandout) => {
            if (!savedHandout?.baseContentId) return null;
            return contentStore?.contentData?.[savedHandout.baseContentId] || null;
        },
        [contentStore],
    );

    const getSavedHandoutImageSrcKey = useCallback(
        (savedHandout) => {
            const baseHandout = getSavedBaseHandout(savedHandout);
            const contentData = baseHandout?.contentData?.['en-UK'];
            if (!contentData || !Array.isArray(contentData)) return null;

            for (const section of contentData) {
                if (!section.blocks) continue;
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) return illustrationBlock.src;
            }
            return null;
        },
        [getSavedBaseHandout],
    );

    const getSavedHandoutTitle = useCallback(
        (savedHandout) => {
            const baseHandout = getSavedBaseHandout(savedHandout);
            return (
                savedHandout.handoutDocumentTitle ||
                savedHandout.handoutContentName ||
                baseHandout?.contentName ||
                'Saved handout'
            );
        },
        [getSavedBaseHandout],
    );

    const getSavedHandoutFirstParagraph = useCallback(
        (savedHandout) => {
            const baseHandout = getSavedBaseHandout(savedHandout);
            const contentData = baseHandout?.contentData?.['en-UK'];
            if (!contentData || !Array.isArray(contentData)) return '';

            for (const section of contentData) {
                if (!section.blocks) continue;
                for (const block of section.blocks) {
                    if (
                        (block.type === 'paragraph' || block.type === 'text' || block.type === 'textBullet') &&
                        block.value
                    ) {
                        return block.value.trim();
                    }
                }
            }

            return '';
        },
        [getSavedBaseHandout],
    );

    const filteredAndSortedHandouts = useMemo(() => {
        const customisedFavouriteIds = userDataStore.userData.customisedHandoutsFavourites || [];
        const savedFavouriteIds = userDataStore.userData.savedHandoutsFavourites || [];

        const getTimestamp = (item) => {
            if (typeof item.lastUpdated === 'string') return new Date(item.lastUpdated).getTime();
            return item.lastUpdated || 0;
        };

        const combinedItems = [
            ...customisedHandouts.map((item) => ({ ...item, type: 'customised' })),
            ...savedHandouts.map((item) => ({ ...item, type: 'saved' })),
        ];

        let items = [...combinedItems];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            items = items.filter((item) => {
                const title =
                    item.type === 'saved'
                        ? getSavedHandoutTitle(item).toLowerCase()
                        : getDocumentTitle(item).toLowerCase();
                const notes = (item.notes || '').toLowerCase();
                return title.includes(term) || notes.includes(term);
            });
        }

        if (sortBy === 'RECENT') {
            items.sort((a, b) =>
                sortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
            );
        } else {
            const favourites = items
                .filter((item) =>
                    item.type === 'saved'
                        ? isSavedHandoutFavourite(
                              item.baseContentId,
                              savedFavouriteIds,
                              savedHandoutStore?.savedHandoutData || {},
                          )
                        : customisedFavouriteIds.includes(item.customisedId),
                )
                .sort((a, b) =>
                    sortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
                );
            const nonFavourites = items
                .filter((item) =>
                    item.type === 'saved'
                        ? !isSavedHandoutFavourite(
                              item.baseContentId,
                              savedFavouriteIds,
                              savedHandoutStore?.savedHandoutData || {},
                          )
                        : !customisedFavouriteIds.includes(item.customisedId),
                )
                .sort((a, b) =>
                    sortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
                );
            items = [...favourites, ...nonFavourites];
        }

        return items;
    }, [
        customisedHandouts,
        savedHandouts,
        searchTerm,
        sortBy,
        sortDirection,
        getSavedHandoutTitle,
        userDataStore.userData.customisedHandoutsFavourites,
        userDataStore.userData.savedHandoutsFavourites,
        savedHandoutStore?.savedHandoutData,
    ]);

    const pageCount = Math.max(1, Math.ceil(filteredAndSortedHandouts.length / itemsPerPage));
    const safePage = Math.min(currentPage, pageCount);
    const visiblePages = getVisiblePages(safePage, pageCount);

    const paginatedHandouts = useMemo(() => {
        const start = (safePage - 1) * itemsPerPage;
        return filteredAndSortedHandouts.slice(start, start + itemsPerPage);
    }, [filteredAndSortedHandouts, safePage, itemsPerPage]);

    // Fetch handout illustration images for the current page
    useEffect(() => {
        let isMounted = true;

        const getImageSrc = (item) => {
            if (item.attachedWhiteboardCanvasImageLink) return item.attachedWhiteboardCanvasImageLink;
            const contentData = item.effectiveContentData?.['en-UK'];
            if (!contentData || !Array.isArray(contentData)) return null;
            for (const section of contentData) {
                if (!section.blocks) continue;
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) return illustrationBlock.src;
            }
            return null;
        };

        const fetchImages = async () => {
            const newImageMap = {};
            let shouldUpdate = false;

            for (const item of paginatedHandouts) {
                const imageSrc = getImageSrc(item);
                if (!imageSrc || fetchedImages.current.has(imageSrc)) continue;

                try {
                    let imageData = null;
                    const cached = await imageStore.getImageFromIndexedDB(imageSrc);
                    if (cached) {
                        imageData = cached.data;
                    } else {
                        const isWhiteboardImage = imageSrc.startsWith('png/');
                        const payload = {
                            files: [{ method: 'get', key: imageSrc }],
                            contentCategory: isWhiteboardImage ? 'whiteboard' : 'image',
                        };
                        const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                        const presignedUrl = resp.getUrls[0].presignedUrl;
                        const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);
                        await imageStore.saveImageToIndexedDB(imageSrc, downloadResult.base64data, downloadResult.eTag);
                        imageData = downloadResult.base64data;
                    }

                    if (isMounted && imageData) {
                        newImageMap[imageSrc] = imageData;
                        fetchedImages.current.add(imageSrc);
                        shouldUpdate = true;
                    }
                } catch (err) {
                    console.error(`Error fetching image: ${imageSrc}`, err);
                }
            }

            if (isMounted && shouldUpdate) {
                setImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        if (paginatedHandouts.length > 0) fetchImages();
        return () => {
            isMounted = false;
        };
    }, [paginatedHandouts, imageMap, imageStore, authStore.idToken]);

    useEffect(() => {
        let isMounted = true;

        const fetchSavedImages = async () => {
            const newImageMap = {};
            let shouldUpdate = false;

            for (const item of paginatedHandouts) {
                if (item.type !== 'saved') continue;
                const imageSrc = getSavedHandoutImageSrcKey(item);
                if (!imageSrc || fetchedSavedImages.current.has(imageSrc)) continue;

                try {
                    let imageData = null;
                    const cached = await imageStore.getImageFromIndexedDB(imageSrc);
                    if (cached) {
                        imageData = cached.data;
                    } else {
                        const payload = {
                            files: [{ method: 'get', key: imageSrc }],
                            contentCategory: 'image',
                        };
                        const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                        const presignedUrl = resp.getUrls[0].presignedUrl;
                        const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);
                        await imageStore.saveImageToIndexedDB(imageSrc, downloadResult.base64data, downloadResult.eTag);
                        imageData = downloadResult.base64data;
                    }

                    if (isMounted && imageData) {
                        newImageMap[imageSrc] = imageData;
                        fetchedSavedImages.current.add(imageSrc);
                        shouldUpdate = true;
                    }
                } catch (err) {
                    console.error(`Error fetching saved image: ${imageSrc}`, err);
                }
            }

            if (isMounted && shouldUpdate) {
                setSavedHandoutImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        if (paginatedHandouts.some((item) => item.type === 'saved')) fetchSavedImages();
        return () => {
            isMounted = false;
        };
    }, [paginatedHandouts, getSavedHandoutImageSrcKey, imageStore, authStore.idToken]);

    // Sort menu handlers
    const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);

    const handleCloseMenu = (option) => {
        if (option === 'RECENT') {
            setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
            setHasSortedRecently(true);
            setSortBy('RECENT');
        }
        if (option === 'FAVOURITES') {
            const isSameSort = sortBy === 'FAVOURITES';
            setSortDirection((prev) => (isSameSort && prev === 'desc' ? 'asc' : 'desc'));
            setHasSortedRecently(false);
            setSortBy('FAVOURITES');
        }
        setAnchorEl(null);
    };

    const handleToggleFavourite = async (customisedId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.customisedHandoutsFavourites?.includes(customisedId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.customisedHandoutsFavourites.filter((id) => id !== customisedId)
            : [...(userDataStore.userData.customisedHandoutsFavourites || []), customisedId];

        if (!isFavourite && (userDataStore.userData.customisedHandoutsFavourites?.length || 0) >= 30) {
            showSnackbar('You can only mark up to 30 customised handouts as favourites.', 'warning');
            return;
        }

        userDataStore.setUserData({ ...userDataStore.userData, customisedHandoutsFavourites: updatedFavourites });

        const payload = { customisedHandoutsFavourites: updatedFavourites };
        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update favourites', err);
        }
    };

    const handleToggleSavedFavourite = async (baseContentId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const currentFavourites = userDataStore.userData.savedHandoutsFavourites || [];
        const { updatedFavourites, limitReached } = toggleSavedHandoutFavourite(
            baseContentId,
            currentFavourites,
            savedHandoutStore?.savedHandoutData || {},
        );

        if (limitReached) {
            showSnackbar('You can only mark up to 30 saved handouts as favourites.', 'warning');
            return;
        }

        userDataStore.setUserData({ ...userDataStore.userData, savedHandoutsFavourites: updatedFavourites });

        const payload = { savedHandoutsFavourites: updatedFavourites };
        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update saved handout favourites', err);
        }
    };

    const handleEditCustomisedHandout = (customisedHandout) => {
        if (!customisedHandout.baseContentId) {
            showSnackbar('Cannot edit: missing base content ID', 'error');
            return;
        }
        localStorage.removeItem('lastWhiteboardState');
        localStorage.setItem('loadCustomisedHandout', customisedHandout.customisedId);
        localStorage.setItem('loadCustomisedHandoutInEditMode', 'true');
        navigate(`/handout/${customisedHandout.baseContentId}`);
    };

    const handleOpenCustomisedHandoutView = (customisedHandout) => {
        if (!customisedHandout?.baseContentId) {
            showSnackbar('Cannot open handout: missing base content ID', 'error');
            return;
        }
        localStorage.removeItem('openInCustomiseMode');
        localStorage.removeItem('loadCustomisedHandoutInEditMode');
        localStorage.setItem('loadCustomisedHandout', customisedHandout.customisedId);
        navigate(`/handout/${customisedHandout.baseContentId}`);
    };

    const handleOpenSavedHandout = (savedHandout) => {
        localStorage.setItem('loadSavedHandout', savedHandout.savedHandoutId);
        navigate(`/handout/${savedHandout.baseContentId}`);
    };

    const handleEditSavedHandout = (savedHandout) => {
        localStorage.removeItem('loadSavedHandout');
        localStorage.setItem('openInCustomiseMode', 'true');
        navigate(`/handout/${savedHandout.baseContentId}`);
    };

    const handleQuickShare = (customisedHandout) => {
        const handoutForShare = {
            ...customisedHandout,
            contentId: customisedHandout.baseContentId,
            contentName: getDocumentTitle(customisedHandout),
        };
        setQuickShareHandout(handoutForShare);
        setQuickShareOpen(true);
    };

    const handleQuickShareSavedHandout = (savedHandout) => {
        const baseHandout = getSavedBaseHandout(savedHandout);
        if (!baseHandout?.contentId) {
            showSnackbar('Cannot share: missing base handout.', 'error');
            return;
        }
        setQuickShareHandout(baseHandout);
        setQuickShareOpen(true);
    };

    const handleArchiveCustomisedHandout = async () => {
        if (!archiveTarget) return;
        if (archiveTarget.type === 'saved') {
            setIsDeletingSavedHandout(true);
            try {
                await UserApi.deleteSavedHandout(authStore.idToken, {
                    savedHandoutId: archiveTarget.savedHandoutId,
                });
                if (typeof savedHandoutStore.removeSavedHandout === 'function') {
                    await savedHandoutStore.removeSavedHandout(archiveTarget.savedHandoutId);
                } else {
                    if (savedHandoutStore?.db?.savedHandouts) {
                        await savedHandoutStore.db.savedHandouts.delete(archiveTarget.savedHandoutId);
                    }
                    if (savedHandoutStore?.savedHandoutData) {
                        delete savedHandoutStore.savedHandoutData[archiveTarget.savedHandoutId];
                    }
                }
                setSavedHandouts((prev) => prev.filter((h) => h.savedHandoutId !== archiveTarget.savedHandoutId));
                showSnackbar('Saved handout deleted');
            } catch (err) {
                console.error('Failed to delete saved handout', err);
                showSnackbar('Failed to delete saved handout', 'error');
            } finally {
                setIsDeletingSavedHandout(false);
                setArchiveTarget(null);
            }
            return;
        }
        await archiveCustomisedHandout(
            archiveTarget,
            userDataStore,
            customisedHandoutStore,
            authStore,
            setCustomisedHandouts,
            () => {},
            showSnackbar,
        );
        setArchiveTarget(null);
    };

    const handleSaveNote = async () => {
        if (!noteTarget) return;
        try {
            if (noteTarget.type === 'saved') {
                await UserApi.createOrUpdateSavedHandout(authStore.idToken, {
                    savedHandoutId: noteTarget.savedHandoutId,
                    baseContentId: noteTarget.baseContentId,
                    notes: noteText,
                });
                const updatedSavedHandout = { ...noteTarget, notes: noteText };
                delete updatedSavedHandout.type;
                await savedHandoutStore.addOrUpdateSavedHandout(updatedSavedHandout);
                setSavedHandouts((prev) =>
                    prev.map((h) => (h.savedHandoutId === noteTarget.savedHandoutId ? updatedSavedHandout : h)),
                );
            } else {
                await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, {
                    customisedId: noteTarget.customisedId,
                    baseContentId: noteTarget.baseContentId,
                    notes: noteText,
                });
                const updatedCustomisedHandout = { ...noteTarget, notes: noteText };
                delete updatedCustomisedHandout.type;
                await customisedHandoutStore.addOrUpdateCustomisedHandout(updatedCustomisedHandout);
                setCustomisedHandouts((prev) =>
                    prev.map((h) => (h.customisedId === noteTarget.customisedId ? updatedCustomisedHandout : h)),
                );
            }
            setNoteTarget(null);
            setNoteText('');
            showSnackbar('Note saved', 'success');
        } catch (err) {
            console.error('Error saving note:', err);
            showSnackbar('Failed to save note', 'error');
        }
    };

    const handleArchivePatient = async (pid) => {
        try {
            const updatedPatient = { ...patient, patientStatus: 'archived' };
            await UserApi.createOrUpdatePatient(authStore.idToken, updatedPatient);
            setPatient(updatedPatient);
            showSnackbar('Patient archived');
        } catch (err) {
            showSnackbar('Failed to archive patient', 'error');
        }
    };

    return (
        <>
            {(loading || isDeletingSavedHandout) && <BlockingSpinner open={true} />}

            <Box
                sx={{
                    px: { xs: '16px', md: '20px' },
                    pb: '24px',
                    background: '#F0F0FC',
                    minHeight: 'calc(100vh - 140px)',
                }}
            >
                <Box sx={{ maxWidth: '1360px', margin: '0 auto', pt: { xs: '20px', md: '34px' } }}>
                    {/* Page title */}
                    <Typography
                        sx={{
                            color: '#000453',
                            fontSize: { xs: '24px', md: '40px' },
                            lineHeight: { xs: '24px', md: '40px' },
                            fontWeight: 600,
                            letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                            mb: { xs: '20px', md: '35px' },
                        }}
                    >
                        Patient record
                    </Typography>

                    {/* Row: patient card (left, 649px) + search pill (right, 251px), bottom-aligned per Figma */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'flex-end' },
                            justifyContent: 'space-between',
                            mb: { xs: '16px', md: '30px' },
                            gap: { xs: '12px', md: 0 },
                        }}
                    >
                        <Box sx={{ width: { xs: '100%', md: '649px' }, flexShrink: 0 }}>
                            {patient && (
                                <PatientSummaryCard
                                    patient={patient}
                                    totalHandoutCount={customisedHandouts.length + savedHandouts.length}
                                    onEdit={(p) => {
                                        setEditingPatient(p);
                                        setEditDialogOpen(true);
                                    }}
                                    onArchive={handleArchivePatient}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                borderRadius: '89.324px',
                                background: 'linear-gradient(176.249deg, #FF669B 0%, #BD8DE8 110.58%)',
                                height: '50px',
                                width: { xs: '100%', md: '251px' },
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                px: '16px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <SearchIcon sx={{ color: '#FFFFFF', opacity: 0.8, fontSize: 20, mr: 1, flexShrink: 0 }} />
                            <InputBase
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search handouts..."
                                fullWidth
                                inputProps={{ spellCheck: false }}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    background: 'transparent',
                                    color: 'white',
                                    '& .MuiInputBase-input': {
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        appearance: 'none',
                                        color: 'white',
                                        fontSize: '16px',
                                        lineHeight: '16px',
                                        letterSpacing: '-0.32px',
                                        fontWeight: 400,
                                        padding: 0,
                                        border: 0,
                                        borderRadius: 0,
                                        outline: 0,
                                        margin: 0,
                                        backgroundColor: 'transparent !important',
                                        boxShadow: 'none',
                                        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                        WebkitTextFillColor: 'white',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: 'rgba(255,255,255,0.95)',
                                        opacity: 1,
                                    },
                                    '& input:-webkit-autofill': {
                                        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                        WebkitTextFillColor: 'white !important',
                                        transition: 'background-color 9999s ease-out 0s',
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Patient library panel — same layout as my-library white panel */}
                    <Box
                        sx={{
                            width: '100%',
                            minHeight: { xs: 'auto', md: '566px' },
                            background: '#FFFFFF',
                            borderRadius: '30px',
                            border: '1px solid #ECECFA',
                            p: { xs: '14px', md: '20px' },
                        }}
                    >
                        {/* Panel header: title + sort */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: { xs: 'flex-start', md: 'space-between' },
                                alignItems: { xs: 'flex-start', md: 'center' },
                                mb: '18px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: '20px', md: '30px' },
                                    lineHeight: { xs: '20px', md: '30px' },
                                    fontWeight: 600,
                                    color: '#000453',
                                    letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                                }}
                            >
                                Patient library
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: '6px', md: 0 } }}>
                                <Typography
                                    onClick={handleOpenMenu}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '12px', md: '14px' },
                                        lineHeight: { xs: '12px', md: '14px' },
                                        letterSpacing: { xs: '1.826px', md: '2px' },
                                        textTransform: 'uppercase',
                                        color: '#1919194D',
                                        cursor: 'pointer',
                                    }}
                                >
                                    SORT BY{' '}
                                    <Box component="span" sx={{ color: '#000453' }}>
                                        {sortBy}
                                    </Box>
                                </Typography>
                                <IconButton onClick={handleOpenMenu} sx={{ ml: 0.5, p: 0 }}>
                                    {sortBy === 'RECENT' && hasSortedRecently ? (
                                        sortDirection === 'desc' ? (
                                            <ExpandMoreIcon
                                                sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                            />
                                        ) : (
                                            <ExpandLessIcon
                                                sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                            />
                                        )
                                    ) : (
                                        <ExpandMoreIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    )}
                                </IconButton>
                            </Box>
                        </Box>

                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleCloseMenu(null)}>
                            <MenuItem onClick={() => handleCloseMenu('RECENT')} sx={{ color: '#000453' }}>
                                RECENT
                            </MenuItem>
                            <MenuItem onClick={() => handleCloseMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                                FAVOURITES
                            </MenuItem>
                        </Menu>

                        {filteredAndSortedHandouts.length === 0 ? (
                            <Box sx={{ mt: 6, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ color: '#000453' }}>
                                    {searchTerm.trim()
                                        ? 'No handouts match your search.'
                                        : 'No handouts assigned to this patient yet.'}
                                </Typography>
                                {!searchTerm.trim() && (
                                    <Typography variant="body1" sx={{ mt: 1, color: '#000453' }}>
                                        To assign a handout, open a handout, select &lsquo;Save handout&rsquo; or
                                        &lsquo;Customise handout&rsquo; and then save to a patient record.
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <>
                                {/* Handout cards — same design as my-library Customised handouts */}
                                <Grid container spacing={2}>
                                    {paginatedHandouts.map((item) => {
                                        const title =
                                            item.type === 'saved' ? getSavedHandoutTitle(item) : getDocumentTitle(item);
                                        const previewText =
                                            item.type === 'saved'
                                                ? getSavedHandoutFirstParagraph(item)
                                                : getFirstParagraph(item);
                                        const imageSrc = getImageSource(item);
                                        const isCustomised = item.type !== 'saved';
                                        const isFavourite = isCustomised
                                            ? userDataStore.userData.customisedHandoutsFavourites?.includes(
                                                  item.customisedId,
                                              )
                                            : isSavedHandoutFavourite(
                                                  item.baseContentId,
                                                  userDataStore.userData.savedHandoutsFavourites || [],
                                                  savedHandoutStore?.savedHandoutData || {},
                                              );

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                md={3}
                                                key={isCustomised ? item.customisedId : item.savedHandoutId}
                                            >
                                                <Box
                                                    onClick={() =>
                                                        isCustomised
                                                            ? handleOpenCustomisedHandoutView(item)
                                                            : handleOpenSavedHandout(item)
                                                    }
                                                    sx={{
                                                        background: '#F0F0FC',
                                                        borderRadius: '20px',
                                                        p: '10px',
                                                        border: '1px solid #ECECFA',
                                                        width: '100%',
                                                        maxWidth: { xs: '100%', md: '314px' },
                                                        mx: { xs: 0, md: 'auto' },
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            background: '#FFFFFF',
                                                            borderRadius: '10px',
                                                            p: '8px',
                                                            minHeight: '248px',
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isCustomised) {
                                                                    handleToggleFavourite(item.customisedId);
                                                                } else {
                                                                    handleToggleSavedFavourite(item.baseContentId);
                                                                }
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '10px',
                                                                right: '10px',
                                                                width: 32,
                                                                height: 32,
                                                                p: 0,
                                                                zIndex: 2,
                                                            }}
                                                        >
                                                            <img
                                                                src={
                                                                    isFavourite
                                                                        ? FavouriteButtonActive
                                                                        : FavouriteButtonDisabled
                                                                }
                                                                alt="Favourite"
                                                                style={{ width: 32, height: 32 }}
                                                            />
                                                        </IconButton>

                                                        {isCustomised && (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '11px',
                                                                    lineHeight: '14px',
                                                                    color: '#A1A1B3',
                                                                    pr: '36px',
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                Updated on:{' '}
                                                                {new Date(item.lastUpdated).toLocaleString('en-GB')}
                                                            </Typography>
                                                        )}

                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                height: '168px',
                                                                borderRadius: '10px',
                                                                overflow: 'hidden',
                                                                background: '#FFFFFF',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            {imageSrc ? (
                                                                <img
                                                                    src={imageSrc}
                                                                    alt={title}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'contain',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography sx={{ color: '#A1A1B3', fontSize: '12px' }}>
                                                                    No preview
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        {/* Note snapshot or Add note pill */}
                                                        <Box
                                                            sx={{
                                                                mt: '8px',
                                                                minHeight: '28px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            {item.notes ? (
                                                                <TruncatedTooltip
                                                                    text={item.notes}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setNoteTarget(item);
                                                                        setNoteText(item.notes);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Box
                                                                    component="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setNoteTarget(item);
                                                                        setNoteText('');
                                                                    }}
                                                                    sx={{
                                                                        border: '1.5px solid #FF669B',
                                                                        borderRadius: '999px',
                                                                        px: '12px',
                                                                        py: '4px',
                                                                        color: '#FF669B',
                                                                        fontSize: '13px',
                                                                        fontWeight: 500,
                                                                        background: 'transparent',
                                                                        cursor: 'pointer',
                                                                        fontFamily: 'Inter, sans-serif',
                                                                        '&:hover': {
                                                                            background: 'rgba(255,102,155,0.08)',
                                                                        },
                                                                    }}
                                                                >
                                                                    Add note
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    <Typography
                                                        sx={{
                                                            color: '#000453',
                                                            fontWeight: 600,
                                                            fontSize: '22px',
                                                            lineHeight: '24px',
                                                            letterSpacing: '-0.36px',
                                                            mt: '10px',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            minHeight: '48px',
                                                        }}
                                                    >
                                                        {title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: '#616063',
                                                            fontSize: '14px',
                                                            lineHeight: '18px',
                                                            mt: '6px',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            minHeight: '36px',
                                                        }}
                                                    >
                                                        {previewText}
                                                    </Typography>

                                                    {/* Card action buttons: share, edit, archive */}
                                                    <Box
                                                        sx={{
                                                            mt: '10px',
                                                            borderTop: '1px solid #D8D8EA',
                                                            pt: '10px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                        }}
                                                    >
                                                        <Tooltip title="Quick share" arrow>
                                                            <Box
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isCustomised) handleQuickShare(item);
                                                                    else handleQuickShareSavedHandout(item);
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    background: '#FF669B',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { background: '#FF85B0' },
                                                                }}
                                                            >
                                                                <img
                                                                    src={ShareEnvelopIcon}
                                                                    alt="Share"
                                                                    width={22}
                                                                    height={22}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                                                        >
                                                            <Tooltip
                                                                title={
                                                                    isCustomised
                                                                        ? 'Open in customise mode'
                                                                        : 'Open handout'
                                                                }
                                                                arrow
                                                            >
                                                                <Box
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (isCustomised) {
                                                                            handleEditCustomisedHandout(item);
                                                                        } else {
                                                                            handleEditSavedHandout(item);
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        width: '50px',
                                                                        height: '50px',
                                                                        borderRadius: '50%',
                                                                        background: '#FF669B',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        cursor: 'pointer',
                                                                        '&:hover': { background: '#FF85B0' },
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={EditHandoutIcon}
                                                                        alt={isCustomised ? 'Edit' : 'Open'}
                                                                        width={22}
                                                                        height={22}
                                                                    />
                                                                </Box>
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    isCustomised ? 'Archive handout' : 'Delete handout'
                                                                }
                                                                arrow
                                                            >
                                                                <Box
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setArchiveTarget(item);
                                                                    }}
                                                                    sx={{
                                                                        width: '50px',
                                                                        height: '50px',
                                                                        borderRadius: '50%',
                                                                        overflow: 'hidden',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        cursor: 'pointer',
                                                                        '&:hover': { filter: 'brightness(1.12)' },
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={BinArchiveIcon}
                                                                        alt="Archive"
                                                                        width={50}
                                                                        height={50}
                                                                    />
                                                                </Box>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* Pagination */}
                                <Box
                                    sx={{
                                        mt: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <IconButton
                                            size="small"
                                            disabled={safePage <= 1}
                                            onClick={() => setCurrentPage((v) => Math.max(1, v - 1))}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '32px',
                                                border: '1.313px solid #E5E5E5',
                                                background: '#FFFFFF',
                                                color: '#6D6D6D',
                                                '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                            }}
                                        >
                                            <ChevronLeftIcon sx={{ fontSize: '15px' }} />
                                        </IconButton>
                                        {visiblePages.map((pageItem, index) => {
                                            if (pageItem === 'ellipsis') {
                                                return (
                                                    <Box
                                                        key={`ellipsis-${index}`}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '8px',
                                                            background: '#FFFFFF',
                                                            color: '#616063',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '16px',
                                                        }}
                                                    >
                                                        ...
                                                    </Box>
                                                );
                                            }
                                            const isActive = pageItem === safePage;
                                            return (
                                                <Box
                                                    key={pageItem}
                                                    onClick={() => setCurrentPage(pageItem)}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '32px',
                                                        border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                                        background: isActive ? '#FF669B' : '#FFFFFF',
                                                        color: isActive ? '#FFFFFF' : '#616063',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '13px',
                                                        fontWeight: isActive ? 400 : 500,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {pageItem}
                                                </Box>
                                            );
                                        })}
                                        <IconButton
                                            size="small"
                                            disabled={safePage >= pageCount}
                                            onClick={() => setCurrentPage((v) => Math.min(pageCount, v + 1))}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '32px',
                                                border: '1.313px solid #E5E5E5',
                                                background: '#FFFFFF',
                                                color: '#6D6D6D',
                                                '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                            }}
                                        >
                                            <ChevronRightIcon sx={{ fontSize: '15px' }} />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Typography
                                            sx={{
                                                color: '#616063',
                                                fontSize: { xs: '14px', md: '13px' },
                                                lineHeight: { xs: '14px', md: '13px' },
                                            }}
                                        >
                                            Items per page
                                        </Typography>
                                        <Select
                                            size="small"
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            sx={{
                                                width: { xs: 63, md: 'auto' },
                                                height: 30,
                                                borderRadius: '10px',
                                                fontSize: '13px',
                                                color: '#616063',
                                                '& .MuiSelect-select': {
                                                    py: 0,
                                                    minHeight: 'unset',
                                                    lineHeight: '13px',
                                                },
                                            }}
                                        >
                                            {[4, 8, 12].map((v) => (
                                                <MenuItem key={v} value={v}>
                                                    {v}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Note dialog */}
            <Dialog open={Boolean(noteTarget)} onClose={() => setNoteTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: '#000453', fontWeight: 700, fontSize: '24px' }}>Add note</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Type a quick note that will be saved with this handout..."
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF669B' },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setNoteTarget(null)} sx={{ color: '#FF669B', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveNote}
                        variant="contained"
                        disabled={!noteText.trim()}
                        sx={{
                            background: '#FF669B',
                            '&:hover': { background: '#FF85B0' },
                            borderRadius: '999px',
                            textTransform: 'none',
                            px: 3,
                        }}
                    >
                        Save note
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Archive confirmation dialog */}
            <Dialog open={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)}>
                <DialogTitle>
                    Are you sure you want to {archiveTarget?.type === 'saved' ? 'delete' : 'archive'} this{' '}
                    {archiveTarget?.type === 'saved' ? 'saved handout' : 'customised handout'}?
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        This action will {archiveTarget?.type === 'saved' ? 'delete' : 'archive'} the{' '}
                        {archiveTarget?.type === 'saved' ? 'saved handout' : 'customised handout'} and remove it from
                        the patient library.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleArchiveCustomisedHandout} color="primary" autoFocus>
                        Yes
                    </Button>
                    <Button onClick={() => setArchiveTarget(null)}>No</Button>
                </DialogActions>
            </Dialog>

            {/* Quick share dialog */}
            {quickShareHandout && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={quickShareHandout}
                    onClose={() => {
                        setQuickShareOpen(false);
                        setQuickShareHandout(null);
                    }}
                />
            )}

            {/* Edit patient dialog */}
            {editingPatient && (
                <AddPatientDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    showSnackbar={showSnackbar}
                    onSubmit={async (updatedData) => {
                        try {
                            const updatedPatient = {
                                ...editingPatient,
                                ...updatedData,
                                patientId: editingPatient.patientId,
                            };
                            await UserApi.createOrUpdatePatient(authStore.idToken, updatedPatient);
                            setPatient(updatedPatient);
                            showSnackbar('Patient updated!', 'success');
                        } catch (err) {
                            console.error('Failed to update patient:', err);
                            showSnackbar('Failed to update patient.', 'error');
                        } finally {
                            setEditDialogOpen(false);
                        }
                    }}
                    initialValues={editingPatient}
                />
            )}
        </>
    );
};

export default observer(MyClinicPatientLibrary);
